import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persist a session id across the tab session (24h). No PII.
const SESSION_KEY = "archive_session_id";
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

const getSessionId = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const { id, ts } = JSON.parse(raw);
      if (id && Date.now() - ts < SESSION_TTL_MS) return id;
    }
  } catch (_) { /* noop */ }
  const id = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now() }));
  } catch (_) { /* noop */ }
  return id;
};

/**
 * AnalyticsTracker — mounts once near the BrowserRouter and fires a
 * pageview each time the path changes. Captures dossier slug when on
 * /topic/<slug>. Sends only path, slug, referrer, session, viewport.
 * Errors are swallowed silently so a broken endpoint never breaks the site.
 */
export const AnalyticsTracker = () => {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;

    // Extract dossier slug if on /topic/<slug>
    let slug = null;
    const m = location.pathname.match(/^\/topic\/([^/]+)/);
    if (m) slug = m[1];

    const payload = {
      path: location.pathname,
      slug,
      referrer: typeof document !== "undefined" ? (document.referrer || null) : null,
      session: getSessionId(),
      viewport: typeof window !== "undefined"
        ? `${window.innerWidth}x${window.innerHeight}` : null,
    };

    // Fire-and-forget. Never block UX.
    axios.post(`${API}/analytics/pageview`, payload, { timeout: 5000 })
      .catch(() => { /* silent */ });
  }, [location.pathname]);

  return null;
};

export default AnalyticsTracker;
